// src/pages/FatwasPage/FatwasPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import Navigation from '../../components/Navigation/Navigation';
import Footer from '../../components/Footer/Footer';
import FatwaCard from '../../components/FatwaCard/FatwaCard';
import Header from '../../components/Header/Header';
import { siteConfig } from '../../config/siteConfig';
import fatwasData from '../../data/fatwas.json';
import './FatwasPage.css';
import SEO from '../../components/SEO/SEO';

interface Fatwa {
  id: string;
  question: string;
  reponse: string;
  reference: string;
  tags: string[];
}

interface Chapitre {
  id: string;
  titre: string;
  fatwas: Fatwa[];
}

interface SearchFilters {
  query: string;
  selectedChapter: string;
  selectedTags: string[];
}

const FatwasPage: React.FC = () => {
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    query: '',
    selectedChapter: '',
    selectedTags: []
  });
  
  const [isVisible, setIsVisible] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  // Récupérer les infos de la page depuis la config
  const pageInfo = siteConfig.categories
    .find(cat => cat.id === 'fatwa')
    ?.subcategories.find(sub => sub.path === '/fatwa/zakat');

  // Extraire tous les tags uniques et compter le total des fatwas
  const { allTags, totalFatwas } = useMemo(() => {
    const tagSet = new Set<string>();
    let total = 0;
    fatwasData.chapitres.forEach(chapitre => {
      total += chapitre.fatwas.length;
      chapitre.fatwas.forEach(fatwa => {
        fatwa.tags.forEach(tag => tagSet.add(tag));
      });
    });
    return {
      allTags: Array.from(tagSet).sort(),
      totalFatwas: total
    };
  }, []);

  // Fonction de recherche et filtrage
  const filteredResults = useMemo(() => {
    let results: Array<{ fatwa: Fatwa; chapitre: Chapitre; score: number }> = [];

    fatwasData.chapitres.forEach(chapitre => {
      chapitre.fatwas.forEach(fatwa => {
        let score = 0;
        let matches = true;

        // Filtre par chapitre
        if (searchFilters.selectedChapter && chapitre.id !== searchFilters.selectedChapter) {
          matches = false;
        }

        // Filtre par tags
        if (searchFilters.selectedTags.length > 0) {
          const hasRequiredTags = searchFilters.selectedTags.every(tag => 
            fatwa.tags.includes(tag)
          );
          if (!hasRequiredTags) {
            matches = false;
          }
        }

        // Recherche textuelle
        if (searchFilters.query.trim()) {
          const query = searchFilters.query.toLowerCase();
          const questionMatch = fatwa.question.toLowerCase().includes(query);
          const reponseMatch = fatwa.reponse.toLowerCase().includes(query);
          const tagsMatch = fatwa.tags.some(tag => tag.toLowerCase().includes(query));
          
          if (questionMatch || reponseMatch || tagsMatch) {
            // Calcul du score de pertinence
            if (questionMatch) score += 3;
            if (reponseMatch) score += 1;
            if (tagsMatch) score += 2;
          } else {
            matches = false;
          }
        }

        if (matches) {
          results.push({ fatwa, chapitre, score });
        }
      });
    });

    // Trier par score de pertinence puis par ID
    return results.sort((a, b) => {
      if (searchFilters.query.trim()) {
        return b.score - a.score;
      }
      return a.fatwa.id.localeCompare(b.fatwa.id);
    });
  }, [searchFilters]);

  const handleSearch = (query: string) => {
    setSearchFilters(prev => ({ ...prev, query }));
  };

  const handleChapterFilter = (chapterId: string) => {
    setSearchFilters(prev => ({ 
      ...prev, 
      selectedChapter: chapterId === prev.selectedChapter ? '' : chapterId 
    }));
  };

  const handleTagClick = (tag: string) => {
    setSearchFilters(prev => ({
      ...prev,
      selectedTags: prev.selectedTags.includes(tag)
        ? prev.selectedTags.filter(t => t !== tag)
        : [...prev.selectedTags, tag]
    }));
  };

  const clearFilters = () => {
    setSearchFilters({
      query: '',
      selectedChapter: '',
      selectedTags: []
    });
  };

  const hasActiveFilters = searchFilters.query || searchFilters.selectedChapter || searchFilters.selectedTags.length > 0;

  // Construire le titre et sous-titre
  const headerTitle = pageInfo 
    ? ["Recueil des Fatwas", `${pageInfo.title} - ${pageInfo.author}`]
    : ["Recueil des Fatwas", "La Zakât - Cheykh Souleymâne El-Rouheyli"];
    
  const headerSubtitle = pageInfo?.description || "100 Questions-Réponses sur l'aumône obligatoire\nTraduit par Mâhî Bâyân";

  return (
  <div className={`fatwas-page ${isVisible ? 'visible' : ''}`}>
    <Navigation />
    
    <SEO 
      title="Fatwas sur la Zakât - Mâhî Bâyân"
      description="100 Questions-Réponses sur la Zakât par Cheykh Souleymâne El-Rouheyli. Recueil de fatwas authentiques traduit par un diplômé de l'Université Islamique de Médine."
      url="https://mahi-bayan.netlify.app/fatwa/zakat"
    />
    
    <div className="page-content">
      <Header 
        mainTitle={headerTitle}
        subtitle={headerSubtitle}
      />

      <main className="fatwas-content">
          {/* Navigation par chapitres */}
          {!hasActiveFilters && (
            <div className="chapters-navigation">
              <div className="chapters-header">
                <h2>📖 Explorer par Chapitres</h2>
                <p>Naviguez facilement dans les {fatwasData.chapitres.length} chapitres de ce recueil</p>
              </div>
              <div className="chapters-grid">
                {fatwasData.chapitres.map((chapitre, index) => (
                  <div
                    key={chapitre.id}
                    className="chapter-card"
                    onClick={() => handleChapterFilter(chapitre.id)}
                  >
                    <div className="chapter-number">
                      {String(index + 1).padStart(2, '0')}
                    </div>
                    <div className="chapter-content">
                      <h3 className="chapter-title">{chapitre.titre}</h3>
                      <div className="chapter-stats">
                        <span className="fatwa-count">
                          {chapitre.fatwas.length} fatwa{chapitre.fatwas.length > 1 ? 's' : ''}
                        </span>
                        <span className="chapter-arrow">→</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section de recherche */}
          <div className="search-section">
            <div className="search-container">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Rechercher une fatwa... (question, réponse, mots-clés)"
                  value={searchFilters.query}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="search-input"
                />
                <div className="search-icon">🔍</div>
              </div>
              
              <button 
                className="filters-toggle"
                onClick={() => setShowFilters(!showFilters)}
              >
                ⚙️ Filtres avancés {hasActiveFilters && <span className="filter-indicator"></span>}
              </button>
            </div>

            {/* Filtres avancés */}
            {showFilters && (
              <div className="filters-panel">
                <div className="filter-group">
                  <h4>Chapitres :</h4>
                  <div className="chapter-filters">
                    {fatwasData.chapitres.map(chapitre => (
                      <button
                        key={chapitre.id}
                        className={`filter-chip ${searchFilters.selectedChapter === chapitre.id ? 'active' : ''}`}
                        onClick={() => handleChapterFilter(chapitre.id)}
                      >
                        {chapitre.titre}
                        <span className="count">({chapitre.fatwas.length})</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="filter-group">
                  <h4>Mots-clés populaires :</h4>
                  <div className="tag-filters">
                    {allTags.slice(0, 20).map(tag => (
                      <button
                        key={tag}
                        className={`filter-chip ${searchFilters.selectedTags.includes(tag) ? 'active' : ''}`}
                        onClick={() => handleTagClick(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {hasActiveFilters && (
                  <button className="clear-filters" onClick={clearFilters}>
                    Effacer tous les filtres
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Résultats */}
          <div className="results-section">
            {/* Breadcrumb pour navigation dans un chapitre */}
            {searchFilters.selectedChapter && (
              <div className="chapter-breadcrumb">
                <button 
                  className="back-to-all"
                  onClick={() => setSearchFilters(prev => ({ ...prev, selectedChapter: '' }))}
                >
                  ← Retour à tous les chapitres
                </button>
                <h2 className="current-chapter">
                  {fatwasData.chapitres.find(c => c.id === searchFilters.selectedChapter)?.titre}
                </h2>
              </div>
            )}

            <div className="results-header">
              <h2>
                {hasActiveFilters 
                  ? `${filteredResults.length} fatwa${filteredResults.length > 1 ? 's' : ''} trouvée${filteredResults.length > 1 ? 's' : ''}`
                  : `${totalFatwas} Fatwas disponibles`
                }
              </h2>
              
              {searchFilters.selectedTags.length > 0 && (
                <div className="active-tags">
                  <span>Filtres actifs :</span>
                  {searchFilters.selectedTags.map(tag => (
                    <button
                      key={tag}
                      className="active-tag"
                      onClick={() => handleTagClick(tag)}
                    >
                      {tag} ✕
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="fatwas-container">
              {filteredResults.length > 0 ? (
                filteredResults.map(({ fatwa, chapitre }) => (
                  <FatwaCard
                    key={fatwa.id}
                    fatwa={fatwa}
                    chapitreTitre={chapitre.titre}
                    onTagClick={handleTagClick}
                  />
                ))
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <h3>Aucune fatwa trouvée</h3>
                  <p>
                    {hasActiveFilters 
                      ? "Essayez de modifier vos critères de recherche ou de supprimer certains filtres."
                      : "Aucune fatwa n'est disponible pour le moment."
                    }
                  </p>
                  {hasActiveFilters && (
                    <button className="clear-filters-btn" onClick={clearFilters}>
                      Voir toutes les fatwas
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer texts={[]} />
      </div>
    </div>
  );
};

export default FatwasPage;
